import { Edition, EpisodeStatus, TermKind } from './catalog';
import type { SourceCategory } from './category';

export interface PersonRef {
  readonly personId: number;
  readonly name: string;
  /** Her photo, from a source's actress index, once downloaded. */
  readonly photo?: FileHash;
}

export interface TermRef {
  readonly termId: number;
  readonly kind: TermKind;
  readonly name: string;
}

/** How the viewer feels about a movie. Every movie has one, defaulted when never set. */
export interface MovieMark {
  readonly favourite: boolean;
  /** 1–5. */
  readonly rating?: number;
  readonly hidden: boolean;
  readonly note?: string;
  readonly inWatchlist: boolean;
  /** The custom lists it is on, by id. */
  readonly listIds: ReadonlyArray<number>;
}

/** Where the viewer left off, rolled up to the movie. */
export interface MovieProgress {
  readonly variantId: number;
  readonly episodeNo: number;
  readonly positionS: number;
  readonly durationS: number;
  readonly finished: boolean;
  readonly updatedAt: number;
}

/**
 * A cached file, by its SHA-256. The web app loads it from `/assets/<sha256>`: the URL
 * changes whenever the file does, so it can be cached for good.
 */
export type FileHash = string;

/** What a grid of movies shows: one cover per movie. */
export interface MovieCard {
  readonly movieId: number;
  readonly code: string;
  readonly title: string;
  readonly cover?: FileHash;
  /** The hover clip. */
  readonly previewVideo?: FileHash;
  readonly durationS?: number;
  readonly releaseDate?: string;
  readonly publishedAt?: number;
  readonly editions: ReadonlyArray<Edition>;
  /** Whether the default variant's episodes all resolved to a working stream. */
  readonly playable: boolean;
  readonly mark: MovieMark;
  readonly progress?: MovieProgress;
}

export interface EpisodeView {
  readonly episodeId: number;
  readonly episodeNo: number;
  readonly name?: string;
  readonly status: EpisodeStatus;
  readonly durationS?: number;
  readonly resolutions: ReadonlyArray<string>;
  /** The scrubbing thumbnails: one tile per minute, and the WebVTT that maps time to tile. */
  readonly previewSprite?: FileHash;
  readonly previewVtt?: FileHash;
}

/** One page about the movie on one source. */
export interface VariantView {
  readonly variantId: number;
  readonly sourceId: string;
  readonly edition: Edition;
  /** The page on the source, for opening it there. */
  readonly url: string;
  readonly durationS?: number;
  readonly publishedAt?: number;
  /** When zyx last fetched this page. */
  readonly indexedAt?: number;
  /** The source no longer has this page; shown only for an offline copy kept of it. */
  readonly gone?: boolean;
  readonly sourceViews?: number;
  readonly sourceLikes?: number;
  readonly cover?: FileHash;
  readonly previewVideo?: FileHash;
  readonly episodes: ReadonlyArray<EpisodeView>;
}

export interface MovieDetail extends MovieCard {
  readonly description?: string;
  readonly primaryVariantId?: number;
  readonly cast: ReadonlyArray<PersonRef>;
  readonly terms: ReadonlyArray<TermRef>;
  readonly variants: ReadonlyArray<VariantView>;
}

export interface PersonDetail extends PersonRef {
  readonly movieCount: number;
  readonly favourite: boolean;
  /**
   * Her page on each source that lists her, as a category a By-category job can walk;
   * only on a single person's detail, not in lists.
   */
  readonly categories?: ReadonlyArray<SourceCategory>;
}

export interface TermDetail extends TermRef {
  readonly movieCount: number;
}

/** The library as a whole, counted when asked for. */
export interface LibraryStats {
  /** Every movie indexed. */
  readonly movies: number;
  /** Those browsing shows: not hidden, and with a variant to default to. */
  readonly browsableMovies: number;
  /** Variants their source still has. */
  readonly variants: number;
  /** Movies holding at least one active variant of each edition. A movie can count under several. */
  readonly editions: Readonly<Record<Edition, number>>;
  /** Episodes of active variants, by the result of their last stream check. */
  readonly streams: Readonly<Record<EpisodeStatus, number>>;
  /** People with a visible movie, as of the last recount. */
  readonly people: number;
  /** Genres, makers and so on with a visible movie, as of the last recount. */
  readonly terms: Readonly<Record<TermKind, number>>;
  /** The movies' running time added up. */
  readonly runtimeS: number;
  /** Cached images and clips on disk. Files are stored by hash, so one reached from two variants counts once. */
  readonly files: number;
  readonly fileBytes: number;
  /** Oldest first; only years that have a dated movie. */
  readonly releasedByYear: ReadonlyArray<{ readonly year: number; readonly movies: number }>;
  /** Movies first indexed each day, by the service's calendar, oldest first; only recent days that have one. */
  readonly addedByDay: ReadonlyArray<{ readonly date: string; readonly movies: number }>;
}

/** A list the viewer made, beside the one watchlist. */
export interface MovieList {
  readonly listId: number;
  readonly name: string;
  readonly movieCount: number;
  /** The cover of the first movie in it. */
  readonly cover?: FileHash;
  readonly createdAt: number;
}

/** A moment inside a video worth coming back to. */
export interface BookmarkView {
  readonly bookmarkId: number;
  readonly code: string;
  readonly title: string;
  readonly variantId: number;
  readonly edition: Edition;
  readonly episodeNo: number;
  readonly positionS: number;
  readonly label?: string;
  readonly createdAt: number;
  readonly cover?: FileHash;
  /** The episode's scrubbing thumbnails, to show the moment itself. */
  readonly previewSprite?: FileHash;
  readonly previewVtt?: FileHash;
}

/** `list` is a custom list's own order; `watchlist` the watchlist's. */
export const MOVIE_SORTS = ['published', 'release', 'added', 'views', 'relevance', 'watchlist', 'list'] as const;

export type MovieSort = (typeof MOVIE_SORTS)[number];

/**
 * - `queued`: waiting for a free download slot.
 * - `downloading`: fetching segments now.
 * - `done`: complete on disk; plays offline and saves as an MP4.
 * - `failed`: gave up after its retries; `error` says why. Retry starts it again.
 * - `cancelled`: stopped by the viewer; what was fetched is kept for a later retry.
 */
export const DOWNLOAD_STATUSES = ['queued', 'downloading', 'done', 'failed', 'cancelled'] as const;

export type DownloadStatus = (typeof DOWNLOAD_STATUSES)[number];

/** One episode of one edition, in one quality, kept on the service's disk. */
export interface DownloadView {
  readonly downloadId: number;
  readonly code: string;
  readonly title: string;
  readonly variantId: number;
  readonly edition: Edition;
  readonly episodeNo: number;
  /** How the stream names the quality: `1280x720`. */
  readonly resolution: string;
  readonly status: DownloadStatus;
  readonly segmentsDone: number;
  readonly segmentsTotal?: number;
  readonly bytes: number;
  readonly durationS?: number;
  readonly error?: string;
  readonly createdAt: number;
  readonly finishedAt?: number;
  readonly cover?: FileHash;
}
